import React, { useState, useEffect } from 'react';
import api from '../api/client';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function isPreviousYear(year) {
  return year < new Date().getFullYear();
}

const FLAG_LABELS = {
  bestSeller:    'Best Seller',
  lightningDeal: 'Deal / Promo',
  newProduct:    'New Product',
  outOfStock:    'Out Of Stock',
};

// Отклонение прогноза от факта со знаком (как в ячейках YoY-сетки):
// −50% = прогноз ниже факта на 50% (занижен), +100% = вдвое выше (завышен)
const forecastDeviationPct = (fc, actual) => {
  if (typeof fc !== 'number' || typeof actual !== 'number') return null;
  if (fc === actual) return 0;
  if (actual <= 0) return fc > 0 ? Infinity : null;
  return Math.round(((fc - actual) / actual) * 100);
};
const renderDeviation = (fc, actual) => {
  const d = forecastDeviationPct(fc, actual);
  if (d == null || d === 0) return null;
  return (
    <span
      className={`info-row-dev ${d < 0 ? 'dev-under' : 'dev-over'}`}
      title={d < 0 ? 'Forecast was LOWER than actual sales (underplanned)' : 'Forecast was HIGHER than actual sales (overplanned)'}
    > · {d > 0 ? '+' : ''}{Math.min(d, 999)}% vs actual</span>
  );
};

const YoyEditPopup = ({
  editPopup,
  loadingEdit,
  onClose,
  onSave,
  onEditPopupChange,
  medianWeeksCount = 4,
  medianEndpoint,        // e.g. '/api/walmart-median-month-sales' — overrides default Amazon endpoint
  periodDeals = [],      // дилы Amazon, пересекающиеся с периодом ячейки (ведёт ИИ-агент)
  forecastHistory = null, // { actual, page, pageAcc, ai, aiAcc } — расшифровка объединённой ячейки
}) => {
  const [medianData, setMedianData]       = useState(null);
  const [loadingMedian, setLoadingMedian] = useState(false);

  const isMonthly     = editPopup.mode === 'monthly';
  const anyFlagActive = editPopup.bestSeller || editPopup.lightningDeal || editPopup.newProduct || editPopup.outOfStock;

  useEffect(() => {
    if (!editPopup.isOpen || !anyFlagActive) {
      setMedianData(null);
      return;
    }

    const asin = editPopup.product?.asin;
    const sku  = editPopup.product?.sku;
    const year = editPopup.year;
    if (!year) return;

    let cancelled = false;
    setLoadingMedian(true);

    // medianEndpoint and medianIdentifierParam come from editPopup state (set by YoyMonthlyView)
    // or from props as fallback
    const resolvedEndpoint    = editPopup.medianEndpoint    || medianEndpoint    || null;
    const resolvedIdParam     = editPopup.medianIdentifierParam || 'asin'; // 'asin' for Amazon, 'sku' for others
    const resolvedIdValue     = resolvedIdParam === 'asin' ? asin : (sku || asin);

    if (!resolvedIdValue) { setLoadingMedian(false); return; }

    let url;
    if (isMonthly) {
      if (!editPopup.month) { setLoadingMedian(false); return; }
      const base = resolvedEndpoint || '/api/amazon-median-month-sales';
      url = `${base}?${resolvedIdParam}=${encodeURIComponent(resolvedIdValue)}&beforeMonth=${editPopup.month}&beforeYear=${year}&count=${medianWeeksCount}`;
    } else {
      if (!editPopup.week) { setLoadingMedian(false); return; }
      // Weekly view currently only supports Amazon
      url = `/api/amazon-median-sales?asin=${encodeURIComponent(asin || '')}&beforeWeek=${editPopup.week}&beforeYear=${year}&count=${medianWeeksCount}`;
    }

    api.get(url)
      .then(({ data: result }) => { if (!cancelled) setMedianData(result.success ? result : null); })
      .catch(e => { if (!cancelled) { console.error('Error fetching median:', e); setMedianData(null); } })
      .finally(() => { if (!cancelled) setLoadingMedian(false); });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editPopup.isOpen, editPopup.bestSeller, editPopup.lightningDeal,
    editPopup.newProduct, editPopup.outOfStock,
    editPopup.week, editPopup.month, editPopup.year,
    editPopup.medianEndpoint, editPopup.medianIdentifierParam,
    medianWeeksCount, isMonthly,
  ]);

  if (!editPopup.isOpen) return null;

  const isForPrevYear = isPreviousYear(editPopup.year);
  const periodLabel   = isMonthly
    ? `${MONTH_NAMES[editPopup.month] || editPopup.month} ${editPopup.year}`
    : `W${editPopup.week} ${editPopup.year}`;

  return (
    <div className="popup-overlay">
      <div className="popup-content popup-content--wide">
        <div className="popup-header">
          <h2>Edit Sales Flags</h2>
          <button className="close-popup" onClick={onClose}>&#10005;</button>
        </div>

        <div className="edit-form">
          <div className="edit-info">
            <p><strong>Product:</strong> {editPopup.product?.productName}</p>
            <p><strong>ASIN:</strong> {editPopup.product?.asin}</p>
            <p><strong>{isMonthly ? 'Month' : 'Week'}:</strong> {periodLabel}</p>
            <p><strong>Type:</strong> {
              editPopup.field === 'historical'   ? 'Historical Data'   :
              editPopup.field === 'previousYear' ? 'Previous Year Data':
              editPopup.field === 'currentMonth' ? 'Current Month'     :
              editPopup.field === 'currentWeek'  ? (isMonthly ? 'Current Month' : 'Current Week') :
              editPopup.field === 'prediction'   ? 'Prediction'        : 'Data'
            }</p>
            {/* Ручная правка числа продаж отключена — значение только для справки */}
            <p><strong>Sales Units:</strong> {editPopup.currentValue === '' ? '—' : editPopup.currentValue}</p>
          </div>

          {editPopup.currentValue === '' && (
            <p className="flag-hint">
              No sales record for this period yet — flags can be set once sales data exists.
            </p>
          )}

          {forecastHistory && (
            <div className="deals-block">
              <label>Forecast history for this {isMonthly ? 'month' : 'week'}</label>
              <div className="info-rows">
                <div className="info-row">
                  <span className="info-row-label">Actual sales</span>
                  <span className="info-row-value">
                    {forecastHistory.actual != null ? forecastHistory.actual.toLocaleString() : '—'}
                  </span>
                </div>
                {forecastHistory.page != null && (
                  <div className="info-row">
                    <span className="info-row-label">Our forecast (made before the {isMonthly ? 'month' : 'week'} ended)</span>
                    <span className="info-row-value">
                      {forecastHistory.page.toLocaleString()}
                      {forecastHistory.pageAcc != null && (
                        <span className="info-row-acc"> · {forecastHistory.pageAcc}% match</span>
                      )}
                      {renderDeviation(forecastHistory.page, forecastHistory.actual)}
                    </span>
                  </div>
                )}
                {forecastHistory.ai != null && (
                  <div className="info-row">
                    <span className="info-row-label">AI forecast (submitted before the {isMonthly ? 'month' : 'week'} ended)</span>
                    <span className="info-row-value">
                      {forecastHistory.ai.toLocaleString()}
                      {forecastHistory.aiAcc != null && (
                        <span className="info-row-acc"> · {forecastHistory.aiAcc}% match</span>
                      )}
                      {renderDeviation(forecastHistory.ai, forecastHistory.actual)}
                    </span>
                  </div>
                )}
              </div>
              <p className="flag-hint">
                Match % = smaller number ÷ bigger number: 100% = exact hit, 50% = off
                by 2× (either way), 25% = off by 4×. The merged table cell shows the
                same numbers: sales with YoY change on top, our forecast and the AI
                forecast with their match % below.
              </p>
            </div>
          )}

          {periodDeals.length > 0 && (
            <div className="deals-block">
              <label>Amazon Deals in this {isMonthly ? 'month' : 'week'}</label>
              <table className="deals-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Dates</th>
                    <th>Status</th>
                    <th>Units sold</th>
                  </tr>
                </thead>
                <tbody>
                  {periodDeals.map((d, i) => (
                    <tr key={i}>
                      <td className="deals-table-type">{d.type || 'Deal'}</td>
                      <td>{d.start_date} – {d.end_date}</td>
                      <td><span className={`deal-pill deal-pill--${d.status}`}>{d.status}</span></td>
                      <td className="deals-table-units">{d.units_sold != null ? d.units_sold.toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {periodDeals.filter(d => d.note).map((d, i) => (
                <p key={i} className="deals-table-note">{d.type || 'Deal'}: {d.note}</p>
              ))}
              <p className="flag-hint">Deals are maintained by the AI agent (incl. sales lift analysis in notes).</p>
            </div>
          )}

          {editPopup.supportsFlags !== false && (
            <div className="form-group">
              <label>Flags</label>
              <div className="flags-checkboxes">
                {Object.entries(FLAG_LABELS).map(([key, label]) => (
                  <label key={key} className="flag-checkbox">
                    <input
                      type="checkbox"
                      checked={!!editPopup[key]}
                      onChange={e => onEditPopupChange(prev => ({ ...prev, [key]: e.target.checked }))}
                    />
                    <span className="checkbox-label">{label}</span>
                  </label>
                ))}
              </div>
              <p className="flag-hint">
                Flags reset automatically at the start of each new year.
                {isForPrevYear && anyFlagActive && (
                  <span className="flag-hint--warn">
                    {' '}Warning: Previous-year {isMonthly ? 'month' : 'week'}: the median of the last {medianWeeksCount} clean {isMonthly ? 'months' : 'weeks'} will be used for forecasts instead of raw sales.
                  </span>
                )}
              </p>
            </div>
          )}

          {editPopup.supportsFlags !== false && anyFlagActive && (
            <div className="median-preview-zone">
              <h4 className="median-preview-title">
                Median Sales Calculation
                {isForPrevYear ? ' - used as replacement in forecasts' : ' - for reference'}
              </h4>

              {loadingMedian ? (
                <p className="median-loading">Calculating...</p>
              ) : medianData && medianData.count > 0 ? (
                <>
                  <p className="median-subtitle">
                    Last <strong>{medianData.count}</strong> clean (flag-free) {isMonthly ? 'months' : 'weeks'} before {periodLabel}:
                  </p>
                  <table className="median-weeks-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>{isMonthly ? 'Month' : 'Week'}</th>
                        <th>Units sold</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isMonthly
                        ? medianData.months.map((m, i) => (
                            <tr key={i}>
                              <td>{m.year}</td>
                              <td>{MONTH_NAMES[m.month] || m.month}</td>
                              <td>{m.units}</td>
                            </tr>
                          ))
                        : medianData.weeks.map((w, i) => (
                            <tr key={i}>
                              <td>{w.year}</td>
                              <td>W{w.week}</td>
                              <td>{w.units}</td>
                            </tr>
                          ))
                      }
                    </tbody>
                  </table>
                  <div className="median-result">
                    Median: <strong className="median-value">{medianData.median}</strong> units
                    {isForPrevYear && (
                      <span className="median-note"> - this replaces raw sales in forecast calculations while the flag is active.</span>
                    )}
                  </div>
                </>
              ) : (
                <p className="median-empty">
                  No clean {isMonthly ? 'months' : 'weeks'} found - median cannot be calculated.
                </p>
              )}
            </div>
          )}

          {editPopup.showSuccessMessage && (
            <div className="upload-status success">Changes saved successfully!</div>
          )}
          {editPopup.showErrorMessage && (
            <div className="upload-status error">{editPopup.showErrorMessage}</div>
          )}

          <div className="popup-buttons">
            <button className="cancel-btn" onClick={onClose} disabled={loadingEdit}>
              Cancel
            </button>
            <button
              className="submit-btn"
              onClick={() => onSave(medianData?.median ?? null)}
              disabled={editPopup.currentValue === '' || editPopup.supportsFlags === false || loadingEdit}
            >
              {loadingEdit ? 'Saving...' : 'Save Flags'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YoyEditPopup;